import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from './queries';
import { api } from './client';
import type { UniversalBundle } from './types';

/**
 * (F2) Selective bundle subscription — pass a `select` projector and the
 * component only re-renders when that projection's output changes (deep
 * equality via react-query's structural sharing).
 *
 * Example:
 *   // Only re-render when entities array changes:
 *   const entities = useBundleSelector(activeId, (b) => b?.canonicalScene?.entities);
 *   // Only re-render when totalRows changes:
 *   const n = useBundleSelector(activeId, (b) => b?.modelRows?.length ?? 0);
 */
export function useBundleSelector<T>(
  modelId: string | null | undefined,
  selector: (bundle: UniversalBundle | undefined) => T,
): UseQueryResult<T, Error> {
  return useQuery({
    queryKey: modelId ? queryKeys.bundle(modelId) : ['bundle', 'idle'],
    queryFn: ({ signal }) => api.bundle(modelId!, signal),
    enabled: Boolean(modelId),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    select: selector,
    // structuralSharing default true — react-query compares deep + reuses
    // previous object reference when content identical, so consumer
    // components don't re-render on unrelated bundle mutations.
  });
}
